import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import * as Sentry from "@sentry/browser";
import { ReCaptchaModule } from 'angular-recaptcha3';
import { SharedGuiSmallModule } from 'sharedGuiSmall/sharedGuiSmall.module';
import { RedirectGlobalService } from 'sharedGuiSmall/sharedServices/redirectGlobal.service';
import { BrixxStartBodyComponent } from "sign-up-app/brixx-start/brixx-start.body.component";
import { ForgottenPasswordComponent } from "sign-up-app/forgotten-password";
import { CustomError } from "sign-up-app/interfaces/CustomError";
import { InviteboxComponent } from "sign-up-app/invite-box/invitebox.component";
import { LoginComponent } from "sign-up-app/login";
import { PaymentFormComponent } from "sign-up-app/payment-form/payment-form.component";
import { ResetPasswordComponent } from "sign-up-app/reset-password";
import { AppComponent } from 'sign-up-app/root';
import { AuthAppService } from "sign-up-app/services/auth.service";
import { CrunchSignUpComponent, SignUpComponent } from 'sign-up-app/sign-up';
import { SignUpHeaderComponent } from "sign-up-app/sign-up-header/sign-up-header.component";
import { VerificationEmailComponent } from 'sign-up-app/verification-email';
import { VerificationExpiredComponent } from 'sign-up-app/verification-expired';
import { VerificationRequiredComponent } from 'sign-up-app/verification-required';
import { XeroCreationComponent } from "sign-up-app/xero-creation";
import { NewPlanForm } from 'sharedFeatures/sharedGui/components/newplan-win/newplan.form';
import { DialogCreationService } from 'sharedFeatures/sharedServices';
import { ModalComponent } from './components/modal/modal.component';
import { SignupInterceptor } from './services/signup.http.interceptor';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { createTranslateLoader } from 'sharedFeatures/translate-loader/translate-loader';
import { HttpClient } from '@angular/common/http';
import { SharedGuiModule } from 'sharedFeatures/sharedGui';

if (window.isBugSendingActive) {
    Sentry.init({
        dsn: 'https://b5cdf56f7f67425480b961e7bc6eb30d@sentry.io/1394926'
    });
}

@Injectable()
export class MyErrorHandler extends ErrorHandler {
    constructor(private injector: Injector) {
        super();
    }

    handleError(error: CustomError) {
        super.handleError(error);

        // Send error
        if (window.isBugSendingActive) {
            // Send error to sentry.io
            Sentry.captureException(error.originalError || error);

            // Send error to Elastic APM (manually send insted of using ApmErrorHandler)
            window['apm'] && window['apm'].captureError(error.originalError || error);
        }

        let router = this.injector.get(Router);

        const url = `${window.location.href} (${router.url})`;

        let errorObj;
        if (error.originalError)
            errorObj = error.originalError;
        else
            errorObj = error;

        window.onerror && window.onerror(error.message, url, -1, -1, errorObj);
    }
}
//TODO remove commented components files
@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        AppComponent,
        SignUpComponent,
        InviteboxComponent,
        XeroCreationComponent,
        LoginComponent,
        VerificationEmailComponent,
        VerificationExpiredComponent,
        VerificationRequiredComponent,
        ForgottenPasswordComponent,
        ResetPasswordComponent,
        BrixxStartBodyComponent,
        PaymentFormComponent,
        SignUpHeaderComponent,
        CrunchSignUpComponent,
        ModalComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot([
            { path: 'sign-up', component: SignUpComponent},
            { path: 'sign-up/:package', component: SignUpComponent },
            { path: 'paid-sign-up/:package/:planscount/:frequency/:currency', component: SignUpComponent },
            { path: 'invite-box', component: InviteboxComponent},
            { path: 'invite-box/:email', component: InviteboxComponent },
            { path: 'verification-email', component: VerificationEmailComponent },
            { path: 'verification-expired', component: VerificationExpiredComponent },
            { path: 'verification-required', component: VerificationRequiredComponent },
            { path: 'xero-creation', component: XeroCreationComponent},
            { path: 'sign-in', component: LoginComponent},
            { path: 'forgotten-password', component: ForgottenPasswordComponent},
            { path: 'reset-password', component: ResetPasswordComponent },
            { path: 'crunch-sign-up', component: CrunchSignUpComponent },
            { path: 'brixx-start', component: BrixxStartBodyComponent },
            { path: 'payment-form', component: PaymentFormComponent }
            
        ]),
        HttpClientModule,
        SharedGuiSmallModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpClient],
            },
        }),
        ReCaptchaModule.forRoot({
            invisible: {
                sitekey: window['reCaptchaPublicKey'],
            },
            normal: {
                sitekey: window['reCaptchaPublicKey'],
            },
            language: 'en'
        }),
        NewPlanForm,
        SharedGuiModule
    ],
    providers: [
        {
            provide: ErrorHandler,
            useClass: MyErrorHandler
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SignupInterceptor,
            multi: true,
        },
        AuthAppService,
        RedirectGlobalService,
        DialogCreationService
    ]
})
export class SignUpAppModule {
}